import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import { CloudCog, Database, MonitorSmartphone, Moon, Sun, X } from 'lucide-react';
import { useDataSource } from '../context/DataSourceContext';
import LicenseInformation from './LicenseInformation';
import LanguageSwitcher from './LanguageSwitcher';

interface SettingsModalProps {
  onClose: () => void;
}

const APP_VERSION = '1.0.0'; // This would typically come from your package.json

const SettingsModal = ({ onClose }: SettingsModalProps) => {
  const { t } = useTranslation();
  const { theme, setTheme, currentDisplayedTheme } = useTheme();
  const { useAwsDynamoDB, setUseAwsDynamoDB } = useDataSource();
  const [fontSize, setFontSize] = useState(() => {
    return localStorage.getItem('fontSize') || '16';
  });

  useEffect(() => {
    // Apply font size to the document root
    document.documentElement.style.fontSize = `${fontSize}px`;
    localStorage.setItem('fontSize', fontSize);
  }, [fontSize]);

  const handleFontSizeChange = (value: string) => {
    const size = parseInt(value);
    if (size >= 12 && size <= 24) {
      setFontSize(value);
    }
  };

  // Helper function to determine button styling based on theme selection
  const getThemeButtonStyles = (buttonTheme: string) => {
    const isSelected = theme === buttonTheme;
    const isDarkMode = currentDisplayedTheme === 'dark';
    
    let baseClasses = "flex flex-col items-center justify-center px-3 py-3 rounded-lg border-2 transition-all";
    
    if (isSelected) {
      return `${baseClasses} ${
        buttonTheme === 'dark' || (buttonTheme === 'auto' && isDarkMode)
          ? 'bg-gray-800 border-blue-500 shadow-md'
          : 'bg-white border-blue-500 shadow-md'
      }`;
    }
    
    return `${baseClasses} bg-gray-50 border-gray-200 dark:bg-gray-700 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('settings')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Theme Settings */}
          <div>
            
            <div className="space-y-4">
              {/* Font Size Setting */}
              <div>
                <label htmlFor="font-size" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('fontSize')}
                </label>
                <div className="flex items-center space-x-3">
                  <input
                    type="range"
                    id="font-size"
                    min="12"
                    max="24"
                    step="1"
                    value={fontSize}
                    onChange={(e) => handleFontSizeChange(e.target.value)}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                  />
                  <span className="text-sm text-gray-900 dark:text-white w-8 text-center">
                    {fontSize}px
                  </span>
                </div>
              </div>

              {/* Theme Setting */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('theme')}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => setTheme('light')}
                    className={getThemeButtonStyles('light')}
                    aria-label={t('lightMode')}
                    title={t('lightMode')}
                  >
                    <Sun 
                      size={24} 
                      className={`mb-1 ${theme === 'light' ? 'text-blue-500' : 'text-gray-700 dark:text-gray-300'}`} 
                    />
                    <span 
                      className={`text-sm font-medium ${
                        theme === 'light' 
                          ? 'text-blue-600 dark:text-blue-400' 
                          : 'text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {t('light')}
                    </span>
                  </button>
                  
                  <button
                    onClick={() => setTheme('dark')}
                    className={getThemeButtonStyles('dark')}
                    aria-label={t('darkMode')}
                    title={t('darkMode')}
                  >
                    <Moon 
                      size={24} 
                      className={`mb-1 ${theme === 'dark' ? 'text-blue-500' : 'text-gray-700 dark:text-gray-300'}`} 
                    />
                    <span 
                      className={`text-sm font-medium ${
                        theme === 'dark' 
                          ? 'text-blue-600 dark:text-blue-400' 
                          : 'text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {t('dark')}
                    </span>
                  </button>
                  
                  <button
                    onClick={() => setTheme('auto')}
                    className={getThemeButtonStyles('auto')}
                    aria-label={t('autoMode')}
                    title={t('autoMode')}
                  >
                    <MonitorSmartphone 
                      size={24} 
                      className={`mb-1 ${theme === 'auto' ? 'text-blue-500' : 'text-gray-700 dark:text-gray-300'}`} 
                    />
                    <span 
                      className={`text-sm font-medium ${
                        theme === 'auto' 
                          ? 'text-blue-600 dark:text-blue-400' 
                          : 'text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {t('auto')}
                    </span>
                  </button>
                </div>
                
                {theme === 'auto' && (
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    {currentDisplayedTheme === 'dark' 
                      ? t('systemPreferenceDark')
                      : t('systemPreferenceLight')
                    }
                  </p>
                )}

                <div className="relative my-6 p-1">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                  </div>
                </div>
                <LanguageSwitcher />

                <div className="relative my-6 p-1">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                  </div>
                </div>


              </div>
            </div>
          </div>

          {/* Data Source Settings */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
              {t('dataSource')}
            </h3>
            
            <div className="bg-gray-100 dark:bg-gray-700 rounded-md p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {useAwsDynamoDB ? (
                    <CloudCog className="h-5 w-5 text-blue-500 mr-2" />
                  ) : (
                    <Database className="h-5 w-5 text-gray-500 mr-2" />
                  )}
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {t('useDynamoDB')}
                  </span>
                </div>
                <div className="relative inline-block w-10 align-middle select-none">
                  <input
                    type="checkbox"
                    name="toggle"
                    id="toggle"
                    checked={useAwsDynamoDB}
                    onChange={() => setUseAwsDynamoDB(!useAwsDynamoDB)}
                    className="sr-only"
                  />
                  <label
                    htmlFor="toggle"
                    className={`block overflow-hidden h-6 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${
                      useAwsDynamoDB ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`block h-6 w-6 rounded-full bg-white shadow transform transition-transform duration-200 ease-in-out ${
                        useAwsDynamoDB ? 'translate-x-4' : 'translate-x-0'
                      }`}
                    ></span>
                  </label>
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {useAwsDynamoDB 
                  ? t('usingDynamoDB') 
                  : t('usingLocalStorage')}
              </p>
            </div>
          </div>
          
          {/* About Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
              {t('about')}
            </h3>
            
            <div className="bg-gray-100 dark:bg-gray-700 rounded-md p-4">
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                <span className="font-semibold">{t('version')}:</span> {APP_VERSION}
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <span className="font-semibold">{t('buildDate')}:</span> {new Date().toLocaleDateString()}
              </p>
              <br/>
              <LicenseInformation />
            </div>
          </div>
        </div>
        
        <div className="flex justify-end p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            {t('saveAndClose')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
